import { css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import { OmegaComponent, events } from '@omegagrid/core';
import { msg } from '@omegagrid/localize';
import { ContextData, LinkItem, LinksViewOptions, ReportContext } from '@reporting/rp-types/analytics';
import { BaseVisualizationEditor } from './baseEditor';
import { style } from './baseEditor.style';
import rp from '@reporting/rp-app-host';
import type { LinksVisualization } from '../links';

/**
 * Editor of the links visualization, displayed as a subeditor inside
 * the dashboard editor ("Visualization" panel).
 */
@customElement('rp-visual-links-editor')
export class LinksVisualizationEditor extends BaseVisualizationEditor<LinksVisualization> {

	static styles = [OmegaComponent.styles, style, css`
		.row.link {
			display: flex;
			flex-direction: row;
			align-items: center;
			gap: 4px;
		}

		.row.link og-input {
			flex: 1;
		}

		.row.link og-button {
			flex: 0 0 20px;
		}

		og-button.add {
			border: 1px dashed var(--og-accent-color);
			width: 100%;
		}
	`];

	get contextData() { return this.reportContext?.context as ContextData }

	get viewOptions(): LinksViewOptions {
		const context = this.contextData;
		if (!context) return {};
		context.view_options ??= {};
		return context.view_options as LinksViewOptions;
	}

	get items(): LinkItem[] {
		this.viewOptions.items ??= [];
		return this.viewOptions.items;
	}

	willUpdate() {
		this.grid = false;
	}

	private changed(refresh = false) {
		this.visualization?.refresh(this.contextData);
		this.visualization?.dispatchEditEvent();
		if (refresh) this.requestUpdate();
	}

	async addLink() {
		const contexts = await rp.ms('analytics').get<ReportContext[]>('namedContext', {
			fields: ['id_context', 'name', 'module', 'context_view'],
			order: [['name', 'asc']],
		});

		const cmd = await rp.host.openMenu({
			text: msg('Select view'),
			content: contexts?.map(c => ({id: c.id_context.toString(), name: `#${c.id_context} ${c.name}`})) ?? [],
			mode: 'search'
		});
		if (cmd?.id == null) return;

		const context = contexts.find(c => c.id_context == parseInt(cmd.id));
		if (!context) return;

		this.items.push({
			id_context: context.id_context,
			module: context.module,
			context_view: context.context_view,
			name: context.name,
		});

		this.changed(true);
	}

	removeLink(index: number) {
		this.items.splice(index, 1);
		this.changed(true);
	}

	_onLabelChange = (e: events.ChangeEvent, link: LinkItem) => {
		link.label = (e.value as string) || undefined;
		this.changed();
	}

	render = () => html`
		${map(this.items, (link, index) => html`
			<div class="row link">
				<og-input
					.placeholder="${link.name || `#${link.id_context}`}"
					.value="${link.label || ''}"
					@change="${(e: events.ChangeEvent) => this._onLabelChange(e, link)}">
				</og-input>

				<og-button
					.icon="${{icon: 'trash', color: 'red'}}"
					color="transparent"
					@click="${() => this.removeLink(index)}">
				</og-button>
			</div>
		`)}

		<div class="row">
			<og-button
				class="add"
				color="transparent"
				@click="${() => this.addLink()}">
				<div slot="content">+ ${msg('Add link')}</div>
			</og-button>
		</div>
	`;

}
